import {StyleSheet, css} from 'aphrodite';
import * as d3 from 'd3';
import React, {useEffect, useState} from 'react';

import {GetAnalysisScratchFile} from 'analysis-tool/AnalysisTypes';
import SignedExtFileURLProxy from 'external-files/SignedExtFileURLProxy';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';
import * as net from 'util/Net';

interface ExternalCSVFileProps {
  scratchFile: GetAnalysisScratchFile;
}

function CSVFileContent({signedURL}: {signedURL: string}): JSX.Element {
  const [csvContent, setCsvContent] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    net
      .getPromise(signedURL)
      .then((content: string) => {
        setCsvContent(content);
      })
      .catch((error: Error) => {
        setErrorMessage('Failed to load CSV file. Please contact Benchling support if the problem persists.');
      });
  }, [signedURL]);

  if (errorMessage) {
    return <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />;
  }

  if (csvContent == null) {
    return <CenteredHorizontalThrobber />;
  }

  const csvRows = d3.csvParseRows(csvContent);

  if (!csvRows || csvRows.length === 0) {
    return <InlineBanner bannerType={InlineBannerType.INFO} text="The CSV file is empty." />;
  }

  const headerRow = csvRows[0];
  const dataRows = csvRows.slice(1);

  return (
    <div className={css(styles.tableWrapper)}>
      <Table
        calculateInView={true}
        className={css(styles.table)}
        emptyTableText="No data"
        fitContent={true}
        frozenHeader={true}
        getRowKey={(rowIndex: number) => rowIndex}
        isCompact={true}
        numRows={dataRows.length}
        overflowVisible={true}
        showBorder={false}
      >
        {headerRow.map((header, columnIndex) => (
          <Column
            key={columnIndex}
            header={header}
            renderCell={(rowIndex: number) => (
              <Cell className={css(styles.cell)}>{dataRows[rowIndex]?.[columnIndex] || ''}</Cell>
            )}
          />
        ))}
      </Table>
    </div>
  );
}

export default function ExternalCSVFile(props: ExternalCSVFileProps): JSX.Element {
  return (
    <SignedExtFileURLProxy
      externalFileId={props.scratchFile.externalFileId!}
      url={props.scratchFile.downloadUrl!}
      renderWithSignedURL={(signedURL) => {
        if (signedURL === undefined) {
          return <CenteredHorizontalThrobber />;
        } else if (signedURL == null) {
          return (
            <InlineBanner
              bannerType={InlineBannerType.ERROR}
              text="Error loading CSV file. Please contact Benchling support if the problem persists."
            />
          );
        } else {
          return <CSVFileContent signedURL={signedURL} />;
        }
      }}
      owningEntityApiId={props.scratchFile.id}
    />
  );
}

const styles = StyleSheet.create({
  tableWrapper: {
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
    border: `1px solid ${GRAY_LIGHT}`,
    borderRadius: 4,
    boxSizing: 'border-box',
  },
  table: {
    borderRadius: 0,
    margin: 0,
  },
  cell: {
    ...FONT_PROXIMA_13,
    height: 36,
  },
});
